<template>
	<div>
		<div class="expression-answer-container d-flex flex-column rounded-lg align-end">
			<div class="form pa-2 w-100 position-relative rounded-lg">
				<ExpressionEditor
					v-if="!isSubmit"
					v-model="textValue"
					:topicSlug="topicSlug"
					@text-change="onTextChange"
				></ExpressionEditor>
				<ExpressionAnswerDisplay v-else :formattedText="computedText" :originalText="textValue" />

				<div class="d-flex justify-end" v-if="!isSubmit && track.track_extension.max_answer_lenght">
					<span
						class="word-count"
						:style="{
							color: this.wordCount < this.track.track_extension.max_answer_lenght ? 'orange' : 'green',
						}"
						>{{ wordCount }} {{ wordCount > 1 ? 'mots' : 'mot' }}</span
					>
				</div>
			</div>
			<div class="d-flex justify-space-between w-100 mt-4" v-if="!isSubmit">
				<BlockAssistant :track="track" :formationProgress="formationProgress" :session="session" v-if="!belowDesktop" />
				<button
					class="submit-button ortho-dark-button font-weight-bold rounded"
					@click="handleAnswerSubmit"
					:disabled="isSubmit || isButtonDisabled"
				>
					Valider
				</button>
			</div>
		</div>
		<v-dialog v-model="isLoading" transition="dialog-fade-transition">
			<v-card class="transparent-card pa-10" flat>
				<v-card-text class="text-center">
					<div class="loading-text mb-6">Ta correction arrive ! 😁</div>
					<v-progress-circular indeterminate color="var(--tag-green-primary)" size="70"></v-progress-circular>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { PROMPT_ASSISTANT_TYPE } from '../../../constants/redaction';
import BlockAssistant from '../../block/BlockAssistant.vue';
import ExpressionEditor from './ExpressionEditor.vue';
import { getTrackResponseByTrackId } from '../../../utils/redaction';
import { countWord } from '@/utils/text.js';
import { stripHtmlTagsWithSpacing, stripSpecificTags } from '@/utils/html.js';
import { CATEGORIES_DONT_WANT_TO_SHOW } from '@/constants/languageTool.js';
import LanguageTool from '@/libraries/language-tool.js';
import ExpressionAnswerDisplay from './ExpressionAnswerDisplay.vue';

export default {
	name: 'ExpressionAnswerContainer',
	components: { BlockAssistant, ExpressionEditor, ExpressionAnswerDisplay },
	props: { track: Object, highlightedError: Object, session: Object, formationProgress: Object },
	data() {
		return {
			textValue: '',
			selectedError: null,
			isSubmit: false,
			isLoading: false,
			errors: [],
			promptAssistantType: PROMPT_ASSISTANT_TYPE,
		};
	},
	computed: {
		topicSlug() {
			return (
				(this.formationProgress &&
					this.formationProgress.config &&
					this.formationProgress.config.topic_id &&
					this.formationProgress.config.topic_id.slug) ||
				''
			);
		},
		belowDesktop() {
			return window.innerWidth <= 648;
		},
		wordCount() {
			return countWord(stripHtmlTagsWithSpacing(this.textValue));
		},
		textWithHighlight() {
			const htmlStringStrippedSomeTags = stripSpecificTags(this.textValue, ['strong', 'em', 'u']); // remove some HTML tags to get plain text
			let text = htmlStringStrippedSomeTags; // String to display, final result after highlighting errors and keywords

			// Highlight errors form Language Tool response
			const plainText = stripHtmlTagsWithSpacing(this.textValue);
			if (this.errors) {
				this.errors.forEach((error, index) => {
					const end = error.offset + error.length;

					// Extract error from textValue
					const errorText = plainText.slice(error.offset, end);

					// Format for selected error from Orthographe component
					if (this.selectedError && this.selectedError.indexMatch === index) {
						const escapedKeyword = errorText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
						const regex = new RegExp(`(?<!\\w)(${escapedKeyword})(?!\\w)`, 'gi');
						text = text.replace(regex, `<span class="highlight-selected-error">${errorText}</span>`);
					} else {
						const escapedKeyword = errorText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
						const regex = new RegExp(`(?<!\\w)(${escapedKeyword})(?!\\w)`, 'gi');
						text = text.replace(regex, `<span class="highlight-error">${errorText}</span>`);
					}
				});
			}

			// Highlight keywords
			const keywords = this.track.track_extension.keywords_expected;
			if (keywords) {
				keywords.forEach((keyword) => {
					const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
					const regex = new RegExp(`(?<!\\w)(${escapedKeyword})(?!\\w)`, 'gi');
					text = text.replace(regex, '<span class="highlight-keyword">$1</span>');
				});
			}

			return text;
		},
		computedText() {
			return this.isSubmit ? this.textWithHighlight : this.textValue;
		},
		isButtonDisabled() {
			const minLength = this.track.track_extension.min_answer_lenght;
			return minLength ? this.wordCount < minLength : this.textValue === '';
		},
	},

	watch: {
		highlightedError: {
			handler(error) {
				this.selectedError = error;
			},
		},
	},

	methods: {
		async submitNewStreakActivity(formationId) {
			await this.$store.dispatch('streak/submitDailyStreakByFormationId', { formationId });
		},
		async fetchLanguageToolCorrection() {
			try {
				delete this.$http.defaults.headers.common['x-auth-token']; // Need this to avoid CORS error

				const responseCheck = await LanguageTool.checkText(
					stripHtmlTagsWithSpacing(this.textValue),
					true,
					'picky',
					'CAT_ANGLICISMES_FOREIGN_TERMS,CAT_ARCHAISMES,CAT_CALQUES,MISC,CAT_PLEONASMES,CAT_REGIONALISMES,STYLE,POLITESSE1,ADVANCED_TYPOGRAPHY,CAT_ANGLICISMES'
				); // Requête vers LanguageTool

				const correctionResponse = [];
				responseCheck.matches.forEach((match) => {
					if (CATEGORIES_DONT_WANT_TO_SHOW.includes(match.rule.category.id)) {
						return;
					}

					correctionResponse.push({
						offset: match.offset,
						length: match.length,
						category: match.rule.category.id,
						message: match.message,
						replacements: match.replacements,
					});
				});

				this.errors = correctionResponse;

				return { correctionResponse };
			} catch (error) {
				return { error: error.message };
			}
		},

		async handleAnswerSubmit() {
			this.isLoading = true;
			let languageToolCorrection = {};
			try {
				const { formationId, sessionId, sessionBlockId } = this.$route.params;
				const trackBodyRequest = {
					formationId,
					sessionId,
					sessionBlockId,
					trackId: this.track._id,
					userAnswers: [this.textValue],
				};

				languageToolCorrection = await this.fetchLanguageToolCorrection();
				if (languageToolCorrection.error == null)
					trackBodyRequest.props = { lang_tool_correction: languageToolCorrection.correctionResponse };

				const [response] = await Promise.all([
					this.$store.dispatch('tracks/createTrackSubmitResponse', trackBodyRequest),
					this.submitNewStreakActivity(formationId),
				]);

				if (response && response.data) {
					const newTrackResponse = response.data.trackResponse;
					await this.$store.dispatch('profile/updateTrackResponse', newTrackResponse);
				}

				this.submitLoading = false;
			} catch (error) {
				this.isLoading = false;
			} finally {
				this.isLoading = false;
				this.isSubmit = true;
				this.$emit('onAnswerSubmit', this.textValue);
				this.$emit('languageToolErrors', this.errors);
				if (languageToolCorrection.error) {
					this.$emit('languageToolAPIFailed', true);
				}
			}
		},
		onTextChange(text) {
			if (text != null) this.textValue = text;
		},
	},
	mounted() {
		const { sessionId, sessionBlockId, trackId } = this.$route.params;

		const trackResponse = getTrackResponseByTrackId(this.formationProgress, sessionId, sessionBlockId, trackId);

		if (trackResponse) {
			this.textValue = trackResponse.user_answers[0];
		}
	},
};
</script>

<style lang="scss" scoped>
.expression-answer-container {
	.form {
		width: 100%;
		border: 1px solid #ddd;

		.word-count {
			font-size: 14px;
			background: white;
			padding: 0 10px;
			border-radius: 3px;
		}
	}
	.submit-button {
		width: fit-content;
		&:disabled {
			cursor: not-allowed;
			opacity: 0.7;
		}
	}
}

::v-deep {
	.highlight-error {
		border-bottom: 2px solid var(--tag-violet-primary);
	}

	.highlight-selected-error {
		background-color: #b1a0ef;
		border-bottom: 2px solid var(--tag-violet-primary);
		color: white;
	}
	.highlight-keyword {
		background-color: var(--tag-green-primary);
		color: white;

		border-radius: 2px;
	}
	.v-dialog {
		justify-content: center;
		align-items: center;
		display: flex;
		margin: 0;
		height: 100%;
		max-height: 100% !important;
	}

	.transparent-card {
		display: flex;
		width: 400px;
		justify-content: center;
		align-items: center;
	}

	.loading-text {
		margin-top: 15px;
		font-size: 18px;
		color: #000;
	}
}

@media only screen and (max-width: 900px) {
	.transparent-card {
		width: 350px;
	}

	.expression-answer-container {
		.submit-button {
			width: 100%;
		}
	}
}
</style>
